import { EvacuationDialog, saveEvacuation, useEvacuation, fmtVolume } from './EvacuationDialog';
import { Leaf } from 'lucide-react';
import { toast } from 'sonner';
import React, { useCallback, useEffect, useState } from 'react';
import { Clock, MapPin, Phone, Navigation, FileText, Camera, AlertTriangle, CalendarRange, PenLine, CheckCircle2, Hourglass, Car, ClipboardCheck } from 'lucide-react';
import { QcBody } from './qualite/qcShared';
import { QualityDetailDialog } from './qualite/QualityDetailDialog';
import { AccTrips, fmtKm } from './AccTrips';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { easerGet, errorMessage, fmtTime, fmtDate, fmtMinutes, reportPhotoUrl } from '../../lib/easerApi';
import { ReportDialog } from './ReportDialog';
import { IncidentDialog } from './IncidentDialog';

export const STATUS = {
  planned: { label: 'À venir', className: 'bg-[hsl(var(--secondary))] text-foreground' },
  in_progress: { label: 'En cours', className: 'bg-[hsl(var(--accent))] text-white' },
  done: { label: 'Terminée', className: 'bg-[hsl(var(--brand-sage))] text-white' },
};

const mapsLink = (client) => client
  ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${client.address} ${client.zip} ${client.town}`)}`
  : null;

const fmtDateTime = (iso) => (iso ? `${fmtDate(iso, { weekday: 'short', day: 'numeric', month: 'short' })} ${fmtTime(iso)}` : '');

/**
 * Full detail of one planning slot: intervention sheet (FI ref, service, planned duration),
 * other slots of the same sheet, client, signature, field reports with photos and incidents.
 */
export function EventDetailDialog({ eventId, onClose, onChanged, onUnauthorized, green = false, user = null }) {
  const [evacEdit, setEvacEdit] = useState(false);
  const [qc, setQc] = useState(null); // contrôles qualité du créneau (manager / Easers concernés)
  const [qcOpen, setQcOpen] = useState(null);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    setQc(null);
    easerGet(`/api/easer/events/${eventId}/qualite`).then((r) => { if (!cancelled) setQc(r.data.items || []); }).catch(() => { if (!cancelled) setQc([]); });
    return () => { cancelled = true; };
  }, [eventId]);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [acc, setAcc] = useState(null); // accompagnements (ACC) du cr\u00e9neau : km + trac\u00e9s GPS

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    setAcc(null);
    easerGet(`/api/easer/events/${eventId}/acc`).then((r) => { if (!cancelled) setAcc(r.data); }).catch(() => { if (!cancelled) setAcc(null); });
    return () => { cancelled = true; };
  }, [eventId]);
  const [reporting, setReporting] = useState(false);
  const [incident, setIncident] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    setError('');
    try {
      const res = await easerGet(`/api/easer/events/${eventId}/details`);
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger le détail'));
    }
  }, [eventId, onUnauthorized]);

  useEffect(() => { setData(null); load(); }, [load]);

  const refresh = () => { load(); if (onChanged) onChanged(); };
  const st = data ? (STATUS[data.status] || STATUS.planned) : null;
  const fi = data?.intervention_detail;

  return (
    <Dialog open={!!eventId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[92vh] overflow-y-auto rounded-2xl p-5" data-testid="event-detail-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-['Space_Grotesk'] pr-6 leading-snug" data-testid="detail-title">{data?.label || 'Détail du créneau'}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {data && (
                <>
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDate(data.start)} · {data.fullday ? 'journée' : `${fmtTime(data.start)} – ${fmtTime(data.end)}`}</span>
                  <Badge className={`${st.className} hover:${st.className}`}>{st.label}</Badge>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {!data && !error && <div className="space-y-3"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-20 rounded-xl" /><Skeleton className="h-16 rounded-xl" /></div>}
        {error && (
          <div className="text-center py-4">
            <p className="text-sm text-destructive mb-3" data-testid="detail-error">{error}</p>
            <Button variant="outline" size="sm" onClick={load}>Réessayer</Button>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Intervention sheet */}
            <section className="rounded-xl border border-border p-3 space-y-2" data-testid="detail-intervention">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Fiche d'intervention</p>
                {fi ? <Badge variant="outline" className="font-mono" data-testid="detail-fi-ref">{fi.ref}</Badge> : <span className="text-xs text-muted-foreground">non liée</span>}
              </div>
              {fi ? (
                <>
                  <p className="text-sm" data-testid="detail-fi-service">{fi.service || fi.description || data.label}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Hourglass className="h-3.5 w-3.5" /> Durée prévue : <strong className="text-foreground" data-testid="detail-planned">{fmtMinutes(data.planned_minutes)}</strong></span>
                    <span>Fiche : {fi.status_label}</span>
                    <span>{fi.slot_count} créneau{fi.slot_count > 1 ? 'x' : ''} au total</span>
                  </div>
                  {fi.description && fi.service && <p className="text-xs text-muted-foreground">{fi.description}</p>}
                  {green && <EvacuationRow ev={{ id: eventId, label: data.label }} onUnauthorized={onUnauthorized} editing={evacEdit} setEditing={setEvacEdit} />}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Durée prévue : <strong className="text-foreground">{data.planned_minutes ? fmtMinutes(data.planned_minutes) : '—'}</strong></p>
              )}
            </section>

            {/* Client */}
            {data.client && (
              <section className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3 text-sm space-y-1" data-testid="detail-client">
                <p className="font-medium">{data.client.name}</p>
                {(data.client.address || data.client.town) && (
                  <p className="text-muted-foreground flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {data.client.address}{data.client.address && ', '}{data.client.zip} {data.client.town}</p>
                )}
                <div className="flex flex-wrap gap-3 pt-1">
                  {data.client.phone && <a href={`tel:${data.client.phone}`} className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium"><Phone className="h-3.5 w-3.5" /> {data.client.phone}</a>}
                  {mapsLink(data.client) && <a href={mapsLink(data.client)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[hsl(var(--primary))] font-medium"><Navigation className="h-3.5 w-3.5" /> Itinéraire</a>}
                </div>
              </section>
            )}
            {data.note && <p className="text-sm text-muted-foreground">{data.note}</p>}

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => setReporting(true)} className="h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="detail-report-btn"><Camera className="h-4 w-4 mr-1" /> Compte-rendu + photos</Button>
              <Button onClick={() => setIncident(true)} variant="outline" className="h-11 border-[hsl(var(--accent))]/50 text-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]" data-testid="detail-incident-btn"><AlertTriangle className="h-4 w-4 mr-1" /> Signaler un imprévu</Button>
            </div>

            {/* Pointage */}
            {data.clock?.in && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="detail-clock">
                <Clock className="h-3.5 w-3.5" /> Pointé {fmtTime(data.clock.in)} → {data.clock.out ? `${fmtTime(data.clock.out)} · ${fmtMinutes(data.clock.minutes)} réalisées` : 'en cours'}
              </p>
            )}

            {/* Signature */}
            {data.signature && (
              <section className="rounded-xl border border-border p-3 text-sm" data-testid="detail-signature">
                <p className="font-medium flex items-center gap-1.5 text-[hsl(var(--brand-sage))]"><CheckCircle2 className="h-4 w-4" /> Signée par {data.signature.client_name || 'le client'} le {fmtDateTime(data.signature.signed_at)}</p>
                {data.signature.comment && <p className="text-muted-foreground mt-1">« {data.signature.comment} »</p>}
                <p className="text-xs text-muted-foreground mt-1">{data.signature.uploaded_to_intervention ? 'Signature déposée sur la fiche d\'intervention Dolibarr.' : 'Signature conservée sur le site.'}</p>
              </section>
            )}

            {/* Accompagnements (bouton ACC) : km factur\u00e9s + trac\u00e9 GPS */}
            {acc && (acc.done?.length > 0 || acc.active) && (
              <section data-testid="detail-accompagnements">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-2"><Car className="h-3.5 w-3.5" /> Accompagnements{acc.total_km > 0 ? ` · ${fmtKm(acc.total_km)} factur\u00e9s` : ''}</p>
                <AccTrips sessions={acc.done} active={acc.active} height={200} compact />
              </section>
            )}

            {/* Contrôle qualité du manager */}
            {qc && qc.length > 0 && (
              <section data-testid="detail-quality">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-2"><ClipboardCheck className="h-3.5 w-3.5" /> Contrôle qualité</p>
                <div className="space-y-2">
                  {qc.map((c) => (
                    <div key={c.id} className="rounded-xl border border-border p-3">
                      <QcBody control={c} compact />
                      <Button variant="outline" size="sm" className="mt-2 h-9 w-full" onClick={() => setQcOpen(c.id)} data-testid={`detail-quality-open-${c.id}`}>
                        {c.reply ? 'Voir le détail' : (c.easer_ids || []).includes(user?.id) ? 'Voir et répondre' : 'Voir le détail'}
                      </Button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Other slots of the same sheet */}
            {data.siblings?.length > 0 && (
              <section data-testid="detail-siblings">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-2"><CalendarRange className="h-3.5 w-3.5" /> Autres créneaux de cette intervention</p>
                <ul className="divide-y divide-border rounded-xl border border-border">
                  {data.siblings.map((s) => {
                    const sst = STATUS[s.status] || STATUS.planned;
                    return (
                      <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span className="capitalize">{fmtDate(s.start, { weekday: 'short', day: 'numeric', month: 'short' })} · {fmtTime(s.start)} – {fmtTime(s.end)}</span>
                        <Badge className={`${sst.className} hover:${sst.className} text-[11px]`}>{sst.label}</Badge>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* Reports */}
            <section data-testid="detail-reports">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-2"><PenLine className="h-3.5 w-3.5" /> Comptes-rendus ({data.reports.length})</p>
              {data.reports.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun compte-rendu pour ce créneau.</p>
              ) : (
                <ul className="space-y-3">
                  {data.reports.map((r) => (
                    <li key={r.id} className="rounded-xl border border-border p-3 text-sm" data-testid="detail-report-item">
                      <p className="text-xs text-muted-foreground">{fmtDateTime(r.created_at)} · {r.uploaded_count}/{r.photos.length} photo{r.photos.length > 1 ? 's' : ''} sur Dolibarr</p>
                      {r.note && <p className="mt-1">{r.note}</p>}
                      {r.photos.length > 0 && (
                        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                          {r.photos.map((p) => (
                            <a key={p.index} href={reportPhotoUrl(r.id, p.index)} target="_blank" rel="noopener noreferrer" className="relative flex-shrink-0">
                              <img src={p.thumb} alt={`Photo ${p.kind_label}`} className="h-20 w-20 object-cover rounded-lg border border-border" />
                              <span className="absolute bottom-1 left-1 rounded bg-black/60 text-white text-[10px] px-1">{p.kind_label}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Incidents */}
            {data.incidents.length > 0 && (
              <section data-testid="detail-incidents">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-2"><AlertTriangle className="h-3.5 w-3.5" /> Imprévus signalés</p>
                <ul className="space-y-2">
                  {data.incidents.map((i, idx) => (
                    <li key={`${i.created_at}-${idx}`} className="rounded-xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/5 p-3 text-sm" data-testid="detail-incident-item">
                      <p className="font-medium">{i.kind_label}{i.delay_min ? ` (~${i.delay_min} min)` : ''} <span className="text-muted-foreground font-normal">· {fmtDateTime(i.created_at)}</span></p>
                      {i.note && <p className="text-muted-foreground mt-0.5">{i.note}</p>}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        <ReportDialog open={reporting} ev={data} onClose={() => setReporting(false)} onDone={() => { setReporting(false); refresh(); }} onUnauthorized={onUnauthorized} />
        <IncidentDialog open={incident} ev={data} onClose={() => setIncident(false)} onDone={() => { setIncident(false); refresh(); }} onUnauthorized={onUnauthorized} />
        <QualityDetailDialog open={!!qcOpen} controlId={qcOpen} userId={user?.id} onClose={() => setQcOpen(null)} onUnauthorized={onUnauthorized}
          onChanged={(c) => setQc((list) => (list || []).map((x) => (x.id === c.id ? c : x)))} />
      </DialogContent>
    </Dialog>
  );
}


/** Green Easers: current green-waste removal of the slot + edit button (Dolibarr « Forfait enlèvement » line). */
function EvacuationRow({ ev, onUnauthorized, editing, setEditing }) {
  const [evac, refresh] = useEvacuation(ev, true);
  if (!evac || evac.unavailable) return null;
  if (!evac.line_id) return null;
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--job-green))]/40 bg-[hsl(var(--job-green))]/10 px-3 py-2 text-sm" data-testid="detail-evacuation">
      <span className="inline-flex items-center gap-1.5"><Leaf className="h-4 w-4 text-[hsl(var(--job-green))]" />
        {evac.has_evacuation ? <>Enlèvement déchets verts : <strong data-testid="detail-evacuation-volume">{fmtVolume(evac.volume_m3)}</strong></> : <span className="text-muted-foreground">Pas d'enlèvement enregistré</span>}
      </span>
      <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(true)} data-testid="detail-evacuation-edit">{evac.has_evacuation ? 'Modifier' : 'Ajouter'}</Button>
      <EvacuationDialog open={editing} ev={ev} mode="edit" existing={evac.volume_m3} onClose={() => setEditing(false)} onUnauthorized={onUnauthorized}
        onProceed={async (volume) => {
          if (volume) {
            try { await saveEvacuation(ev, volume, { onUnauthorized }); toast.success(`Enlèvement enregistré : ${fmtVolume(volume)}`); } catch { return; }
          }
          setEditing(false);
          refresh();
        }} />
    </div>
  );
}
